import {
  EAppSupportType,
  ECRUDDataSourceType,
  ECRUDFormLayoutType,
  ECRUDFormNavigationType,
  EDatasourceType,
  EProjectType,
  EScreenType,
  ESdChangeReason,
  ICRUDRequest,
  IDatabaseTableModel,
  IDatasourceDto,
  IImageLibraryDto,
  IResponsiveLayoutSizes,
  IScreenDesignDto,
  IScreenDesignPublicJSON,
  IScreenTemplate,
  IScreenTemplateCategory,
  IScreenTemplateItem
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import masterScreenTemplatesJson from "../../../../json/masterScreenTemplates.json";
import screenTemplatesJson from "../../../../json/screenTemplates.json";
import { CrudService } from "../../../../services/crud-service";
import { DataDiagramSchemeService } from "../../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../../services/datasource-service";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { IPureDesignComponentProps, IPureDesignComponentState, PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaFileInput from "../../../ui-kit/kuika-file-input/kuika-file-input";
import { shakeInvalidFields } from "../../../ui-kit/kuika-form-page/form-shake";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { SchemaNode } from "../datasource-panel/datasources";
import { DefaultLayout, MasterLayout } from "./screen-template-layout";

/** Create makes a screen; it does not save one. */
const CREATE_SCREEN_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

const screenTemplates = screenTemplatesJson as unknown as IScreenTemplateCategory[];
const masterScreenTemplates = masterScreenTemplatesJson as unknown as IScreenTemplateCategory[];

enum EScreenAuthorizationType {
  Anonymous = 0,
  AllRoles = 1
}

export interface IScreenAddState {
  /** The create is running — the body stays locked behind its spinner meanwhile. */
  isSubmitButtonLoading: boolean;
  /** Where Create stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  selectedTemplateCategory?: IScreenTemplate;
  selectedTemplate?: IScreenTemplateItem;
  dataSourceType: ECRUDDataSourceType;
  schema: SchemaNode[];
  hasExcelImported: boolean;
  importedTables: IDatabaseTableModel[];
  isFileImporting: boolean;
  excelFileName: string;
  searchText: string;
  selectedAuth: EScreenAuthorizationType;
  selectedDeviceType: EAppSupportType;
  isSchemaLoading: boolean;
  responsiveLayoutSizes: IResponsiveLayoutSizes;
}

export interface IScreenAddProps {
  closeModal: (newScreenId?: any) => void;
  showModal: boolean;
  projectId: string;
  componentSetId: string;
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  creatingScreenType: EScreenType;
  isOpenedFromLeftPanel: boolean;
}

class ScreenAddComponent extends PureSdComponent<
  IScreenAddProps & IWithSDProps & IWithHistoryProp & IWithContextProp,
  IScreenAddState
> {
  /**
   * Create's state machine: the ✓ is held on Create, then the dialog closes and the new screen
   * opens. `canStart` keeps a second Create (a duplicate screen) out while the ✓ is up.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IScreenAddProps & IWithSDProps & IWithHistoryProp & IWithContextProp) {
    super(props);
    this.state = {
      isSchemaLoading: false,
      schema: [],
      dataSourceType: ECRUDDataSourceType.Table,
      selectedTemplate:
        this.props.creatingScreenType === EScreenType.Master
          ? masterScreenTemplates[0].templates[0].items[0]
          : screenTemplates[0].templates[0].items[0],
      isSubmitButtonLoading: false,
      saveStatus: "idle",
      selectedTemplateCategory:
        this.props.creatingScreenType === EScreenType.Master
          ? masterScreenTemplates[0].templates[0]
          : screenTemplates[0].templates[0],
      hasExcelImported: false,
      importedTables: [],
      isFileImporting: false,
      excelFileName: "",
      searchText: "",
      selectedAuth: EScreenAuthorizationType.AllRoles,
      selectedDeviceType: EAppSupportType.All,
      responsiveLayoutSizes: {
        desktop: ECRUDFormLayoutType.TwoColumn,
        tablet: ECRUDFormLayoutType.TwoColumn,
        mobile: ECRUDFormLayoutType.SingleColumn
      }
    };
  }

  private formRef = React.createRef<FormInstance>();
  private nameInputRef = React.createRef<HTMLInputElement>();
  /** The dialog body, so a failed submit can shake the fields inside it. */
  private contentRef = React.createRef<HTMLDivElement>();

  componentDidMount = async () => {
    this.loadFormDefaultValues();
    this.setSelectedCategory(
      this.props.creatingScreenType === EScreenType.Master
        ? masterScreenTemplates[0].templates[0]
        : screenTemplates[0].templates[0]
    );
  };

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IScreenAddProps & IWithHistoryProp>,
    prevState: Readonly<IPureDesignComponentState & IScreenAddState>,
    snapshot?: any
  ): void {
    if (prevProps.showModal !== this.props.showModal && this.props.showModal === true) {
      this.loadFormDefaultValues();
      if (this.state.selectedTemplate?.name !== "Blank") {
        this.setSelectedCategory(
          this.props.creatingScreenType === EScreenType.Master
            ? masterScreenTemplates[0].templates[0]
            : screenTemplates[0].templates[0]
        );
      }
      setTimeout(() => {
        this.nameInputRef.current?.focus();
      }, 100);
    }
    if (prevProps.creatingScreenType !== this.props.creatingScreenType) {
      this.setSelectedCategory(
        this.props.creatingScreenType === EScreenType.Master
          ? masterScreenTemplates[0].templates[0]
          : screenTemplates[0].templates[0]
      );
    }
  }

  componentWillUnmount = () => {
    this.saveStatus.dispose();
  };

  loadFormDefaultValues = () => {
    if (this.formRef?.current) {
      let defaultValues: any = {};
      defaultValues.crud = {
        dataSourceType: ECRUDDataSourceType.Table,
        formLayoutType: ECRUDFormLayoutType.SingleColumn,
        hasCreate: true,
        hasDeletion: true,
        hasListPage: true,
        hasPagination: true,
        hasSearchBar: true,
        hasUpdate: true,
        hasView: true,
        hasFormPage: true,
        name: "",
        masterId:
          this.props.sd.getScreenType() === EScreenType.Master && !this.props.isOpenedFromLeftPanel
            ? this.props.sd.sdId
            : undefined,
        navigationType: ECRUDFormNavigationType.Drawer
      };

      this.formRef?.current.resetFields();
      this.formRef?.current.setFieldsValue(defaultValues);
    }
  };

  getSchema = async (dsId?: string) => {
    if (!dsId) {
      this.setState({ schema: [] });
      this.formRef.current?.setFieldsValue({ crud: { tableName: undefined } });
      return;
    }

    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
    try {
      this.setState({ isSchemaLoading: true });
      const treeData: Array<SchemaNode> = [];
      const response = await DatasourceService.GetSchema(dsId);
      if (response.status === 200) {
        for (let index = 0; index < response.data.tables.length; index++) {
          const tbl = response.data.tables[index];
          if (!defaultKuikaTableNames.includes(tbl.name)) {
            treeData.push({
              title: tbl.name,
              schema: tbl.schema,
              key: tbl.id,
              children: tbl.columns.map((x) => {
                return { title: x.name, key: x.id, isLeaf: true, children: [] };
              }),
              isLeaf: false
            });
          }
        }
      }
      this.formRef.current?.setFieldsValue({ crud: { tableName: undefined } });
      this.setState({ schema: treeData });
    } catch {
    } finally {
      this.setState({ isSchemaLoading: false });
    }
  };

  selectScreenDesignTemplate = (template) => {
    this.setState({ selectedTemplate: template });
    const category = this.state.selectedTemplateCategory;
    let name = `${category?.name} ${template?.name}`;
    if (category && (template?.name === category?.name || template?.name.startsWith(category?.name))) {
      name = template.name;
    }
    this.formRef.current?.setFieldsValue({ name });
  };

  setSelectedCategory = (template: IScreenTemplate) => {
    this.state.selectedTemplateCategory?.name === "From Excel";
    this.getSchema();
    this.formRef.current?.setFieldsValue({ crud: { tableName: undefined } });
    this.formRef.current?.setFieldsValue({ crud: { datasourceId: undefined } });
    if (template.name !== "CRUD Wizard" && template.name !== "From Excel") {
      let name = `${template?.name} ${template.items[0]?.name}`;
      if (template?.name === template.items[0]?.name || template?.name.startsWith(template.items[0]?.name)) {
        name = template.items[0].name;
      }
      this.formRef.current?.setFieldsValue({ name });
    } else {
      this.formRef.current?.setFieldsValue({ name: "" });
    }
    this.setState({
      selectedTemplateCategory: template,
      selectedTemplate: template.items[0],
      searchText: "",
      hasExcelImported: false,
      importedTables: [],
      excelFileName: ""
    });
  };

  getMiddleThreeDotOnOverflowTexts = (str) => {
    if (str.length > 21) {
      return `${str.substr(0, 14)}...${str.substr(str.length - 5, str.length)}`;
    }
    return str;
  };

  getDiList = () => {
    if (
      this.props.creatingScreenType === EScreenType.Default ||
      this.props.creatingScreenType === EScreenType.WebScreen ||
      this.props.creatingScreenType === EScreenType.Report
    ) {
      return DefaultLayout;
    }
    if (this.props.creatingScreenType === EScreenType.Master) {
      return MasterLayout;
    }
    return "";
  };

  onSubmit = async (data: any) => {
    if (!this.saveStatus.canStart) return;
    try {
      this.saveStatus.start();
      this.setState({ isSubmitButtonLoading: true });

      data.name = data.name.trim();

      const isCrud = this.state.selectedTemplateCategory?.name === "CRUD Wizard";
      const isExcel = this.state.selectedTemplateCategory?.name === "From Excel";
      const isCrudOrExcel = isCrud || isExcel;
      const isEqualToDsName = this.props.sd?.datasources?.find(
        (ds) => ds.name.toLowerCase() === data.name.toLowerCase()
      );
      const isEqualToWfName = this.props.sd?.workFlowList?.find(
        (wf) => wf.name.toLowerCase() === data.name.toLowerCase()
      );
      let isEqualToTableName = false;

      const managedDBs = this.props.sd.datasources.filter(
        (ds) =>
          ds.datasourceType === EDatasourceType.ManagedDb ||
          ds.datasourceType === EDatasourceType.ManagedDbOracle ||
          ds.datasourceType === EDatasourceType.ManagedDbPostgre
      );
      for (const ds of managedDBs) {
        const response = await DatasourceService.GetSchema(ds.id);
        for (const table of response.data.tables) {
          if (table.name.toLowerCase() === data.name.toLowerCase()) {
            isEqualToTableName = true;
            break;
          }
        }
        if (isEqualToTableName) break;
      }

      if (!isCrudOrExcel && (isEqualToDsName || isEqualToWfName || isEqualToTableName)) {
        this.setState({ isSubmitButtonLoading: false });
        this.saveStatus.reset();
        KMainFunctions.displayWarningNotification(
          "Screen name can not be equal to the datasource, table or process name."
        );
        return;
      }

      await this.props.sd.saveScreen(true);

      const { sd } = this.props;
      const screenList = sd?.screenList || [];
      const isEqualToScreenName = screenList.find((screen) => screen.name.toLowerCase() === data.name.toLowerCase());

      if (isEqualToScreenName) {
        let count = 1;
        const originalName = data.name;

        while (screenList.some((screen) => screen.name.toLowerCase() === data.name.toLowerCase())) {
          data.name = originalName + count;
          count++;
        }
      }

      if (data?.crud && data?.crud.navigationType === undefined) {
        data.crud.navigationType = ECRUDFormNavigationType.Drawer;
      }
      if (data?.crud && data?.crud.dataSourceType === undefined) {
        data.crud.dataSourceType = this.state.dataSourceType;
      }

      let res: any | undefined;
      if (isCrudOrExcel) {
        if (!data.crud.tableName) {
          this.setState({ isSubmitButtonLoading: false });
          this.saveStatus.reset();
          KMainFunctions.displayWarningNotification("Please select a datasource and table");
          return;
        }
        if (this.state.selectedTemplateCategory?.name === "CRUD Wizard" && !data.crud.datasourceId) {
          this.setState({ isSubmitButtonLoading: false });
          this.saveStatus.reset();
          KMainFunctions.displayWarningNotification("Please select a datasource");
          return;
        }
        let obj = {} as ICRUDRequest;
        obj = data.crud;
        const manDb = this.getManagedDB();
        if (this.state.selectedTemplateCategory?.name === "From Excel" && manDb) {
          obj.datasourceId = manDb.id;
        }
        obj.name = data.name;
        obj.projectId = this.props.sd.project?.id;
        obj.responsiveLayoutSizes = this.state.responsiveLayoutSizes;
        obj.screenType = this.props.creatingScreenType ?? EScreenType.Default;
        const formData = this.formRef?.current?.getFieldsValue();
        let screensCount = 0;
        screensCount = this.props.sd.screenList.filter(
          (screen) =>
            screen.publicMetaData.screenType !== EScreenType.Report &&
            screen.publicMetaData.screenType !== EScreenType.Master
        ).length;
        if (formData.crud.hasListPage) screensCount++;
        if (formData.crud.hasCreate) screensCount++;
        if (formData.crud.hasView) screensCount++;
        const accessName = "Screens";
        if (KMainFunctions.isDomainHasAccess(accessName, screensCount - 1) === false) {
          this.props.showDomainAccessRequiredModal(accessName);
          this.setState({ isSubmitButtonLoading: false });
          this.saveStatus.reset();
          return;
        }
        res = await CrudService.createCRUD(obj);
        await this.props.sd.loadActionDefinitions();
      } else {
        const obj = {} as IScreenDesignDto;
        obj.name = data.name;
        if (!data.devices) {
          data.devices = { desktop: true, tablet: true, phone: true };
        }
        obj.kProject_Id = this.props.projectId;
        let isAbsoluteDesignText = "";
        let authText = "";
        if (data.isAbsoluteDesign && data.isAbsoluteDesign === true) {
          isAbsoluteDesignText = `,"isAbsoluteDesign": true`;
        }
        if (this.state.selectedAuth === EScreenAuthorizationType.Anonymous) {
          authText = `,"authorization": { "permittedRoles": [], "allowAnonymousAccess": true, "allowAllRoles": true,"hide":false,"disable":false}`;
        } else if (this.state.selectedAuth === EScreenAuthorizationType.AllRoles) {
          authText = `,"authorization": { "permittedRoles": [], "allowAnonymousAccess": false,"allowAllRoles":true,"hide":true,"disable":false}`;
        }
        if (!isCrud && !isExcel && this.state.selectedTemplate?.name !== "Blank") {
          if (this.state.selectedTemplate?.sdId !== undefined && this.state.selectedTemplate?.sdId.length > 0) {
            obj.templateId = this.state.selectedTemplate?.sdId;
          }
        }
        obj.json = `{"projectId":"${
          this.props.projectId
        }"${isAbsoluteDesignText},"approveInfo":null,"id":"@SDID","name":"${
          obj.name
        }","displayItems":[${this.getDiList()}],"versionNumber":0,"devices":${JSON.stringify(
          data.devices
        )}${authText}}`;
        const publicJson: IScreenDesignPublicJSON = {
          screenType: this.props.creatingScreenType ?? EScreenType.Default,
          masterId:
            this.state.selectedTemplate?.name === "Blank"
              ? this.props.sd.getScreenType() === EScreenType.Master && !this.props.isOpenedFromLeftPanel
                ? this.props.sd.sdId
                : data.masterId
              : undefined,
          screenInputs: [],
          screenOutputs: []
        };
        if (publicJson.masterId) {
          publicJson.supportOffline = this.props.sd.screenList.find(
            (s) => s.id === publicJson.masterId
          )?.publicMetaData.supportOffline;
        }
        obj.publicJson = JSON.stringify(publicJson);
        res = await ScreenService.Create(obj);
      }

      this.setState({ isSubmitButtonLoading: false });
      if (res && res.status === 200) {
        // Create holds its ✓, then the dialog closes and the new screen opens.
        const screenId: string = res.data.screenId ?? res.data;
        this.saveStatus.succeed((): void => this.props.closeModal(screenId));
      } else {
        this.saveStatus.fail();
      }
    } catch (error) {
      this.setState({ isSubmitButtonLoading: false });
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  // Create submits through `formRef.current.submit()`, which never dispatches a DOM submit event,
  // so the document-level shake listener cannot hear it. The dialog reports its own failure.
  onFinishFailed = (): void => {
    const content: HTMLDivElement | null = this.contentRef.current;
    if (content === null) {
      return;
    }
    shakeInvalidFields(content);
  };

  getManagedDbDataSourceId = () => {
    return "";
  };

  handleExcelFiles = (files: File[]): void => {
    const file: File | undefined = files[0];
    if (!file) {
      return;
    }
    this.readJsonFile(file, (jsonStr: string): void => {
      const data = {} as IImageLibraryDto;
      data.fileName = file.name;
      data.content = jsonStr;
      const allData = [] as IImageLibraryDto[];
      allData.push(data);
      this.addExcelFile(allData);
    });
  };

  readJsonFile = (file: File, callback: (result: string) => void): void => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(file);
  };

  async addExcelFile(data) {
    try {
      this.setState({ isFileImporting: true });
      const obj = {
        base64Str: data[0].content
      };
      const res = await DataDiagramSchemeService.CreateSchemaFromExcel(obj);
      if (res && res.status === 200) {
        if (!res.data.tables || res.data.tables.length === 0) {
          KMainFunctions.displayWarningNotification("Excel file is empty. Please provide a valid Excel file.");
          this.setState({ isFileImporting: false });
          return;
        }
        this.formRef.current?.setFieldsValue({
          datasourceId: this.getManagedDbDataSourceId(),
          crud: {
            tableName: res.data?.tables?.[0]?.name
          },
          name: res.data?.tables?.[0]?.name
        });
        this.setState({ hasExcelImported: true, importedTables: res.data.tables, excelFileName: data[0].fileName });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    this.setState({ isFileImporting: false });
  }

  handleStyleFormFieldsChange = async (changedFields: any[], allFields: any[]): Promise<void> => {
    if (!changedFields || changedFields.length === 0) {
      return;
    }
    const formData = this.formRef.current?.getFieldsValue();
    const { value } = changedFields[0];
    if (changedFields[0].name[0] === "crud" && changedFields[0].name[1] === "dataSourceType") {
      this.setState({ dataSourceType: value }, () => {
        if (value == ECRUDDataSourceType.Table || value == ECRUDDataSourceType.Excel) {
          this.formRef.current?.setFieldsValue({
            datasourceId: this.getManagedDbDataSourceId(),
            crud: {
              tableName: undefined
            }
          });
        } else {
          this.formRef.current?.setFieldsValue({
            selectActionId: undefined,
            getByIdActionId: undefined,
            createActionId: undefined,
            updateActionId: undefined,
            deleteActionId: undefined
          });
        }
      });
    } else if (
      changedFields[0].name[0] === "crud" &&
      (changedFields[0].name[1] === "hasDeletion" ||
        changedFields[0].name[1] === "hasSearchBar" ||
        changedFields[0].name[1] === "hasPagination")
    ) {
      if (value === true) {
        formData.crud.hasListPage = value;
        this.formRef.current?.setFieldsValue(formData);
      }
    } else if (changedFields[0].name[0] === "crud" && changedFields[0].name[1] === "hasListPage") {
      formData.crud.hasDeletion = value;
      formData.crud.hasSearchBar = value;
      formData.crud.hasPagination = value;
      this.formRef.current?.setFieldsValue(formData);
    } else if (
      changedFields[0].name[0] === "crud" &&
      (changedFields[0].name[1] === "hasCreate" || changedFields[0].name[1] === "hasUpdate") &&
      value === true
    ) {
      if (!formData.crud.hasFormPage) {
        formData.crud.hasFormPage = true;
        this.formRef.current?.setFieldsValue(formData);
      }
    } else if (changedFields[0].name[0] === "crud" && changedFields[0].name[1] === "hasFormPage") {
      if (value === false) {
        formData.crud.hasCreate = false;
        formData.crud.hasUpdate = false;
        this.formRef.current?.setFieldsValue(formData);
      }
    }
  };

  handleChangeDataSource = (value) => {
    this.getSchema(value);
    this.formRef.current?.resetFields([["crud", "tableName"]]);
  };

  getManagedDB = () => {
    const managedDB = this.props.sd?.datasources?.find((d) => d.connectionString == "");
    if (managedDB) {
      return managedDB;
    }
  };

  handleChangeTable = (value: string) => {
    var pageName = value;
    if (value.indexOf(".") > -1) {
      pageName = value.split(".")[1];
    }
    this.formRef.current?.setFieldsValue({ name: pageName, crud: { tableName: value } });
  };

  deleteSearchText = () => this.setState({ searchText: "" });

  handleSearchFocus = (event) => event.target.select();

  onAuthChange = (value: string | number | Array<string | number> | null) => {
    if (value !== null) {
      this.setState({ selectedAuth: value as EScreenAuthorizationType });
    }
  };

  onDeviceTypeChange = (value) => {
    this.setState({ selectedDeviceType: value });
  };

  getDataSourceName = (datasource: IDatasourceDto) => {
    if (datasource.datasourceType == EDatasourceType.ManagedDb) {
      return "Managed DB (SQL Server)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbOracle) {
      return "Managed DB (Oracle)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbPostgre) {
      return "Managed DB (Postgre)";
    }

    return datasource.name ?? "SCHEMA";
  };

  closeModal = () => {
    // A create that is running or holding its ✓ closes the dialog itself, onto the new screen.
    if (!this.saveStatus.canStart) return;
    this.props.closeModal();
    this.setState(
      {
        selectedTemplate:
          this.props.creatingScreenType === EScreenType.Master
            ? masterScreenTemplates[0].templates[0].items[0]
            : screenTemplates[0].templates[0].items[0],
        selectedTemplateCategory:
          this.props.creatingScreenType === EScreenType.Master
            ? masterScreenTemplates[0].templates[0]
            : screenTemplates[0].templates[0]
      },
      this.getSchema
    );
  };

  renderNotFoundContent = (isCrud: boolean) => {
    if (this.state.isSchemaLoading) {
      return "Loading...";
    }

    return isCrud ? "No tables found" : undefined;
  };

  private renderLayoutOption = (layout: "desktop" | "tablet" | "mobile") => {
    const layoutLabels = {
      [ECRUDFormLayoutType.SingleColumn]: "Single Column",
      [ECRUDFormLayoutType.TwoColumn]: "Two Columns",
      [ECRUDFormLayoutType.FourColumn]: "Four Columns"
    };

    return (
      <KuikaSelectbox
        className={`${layout}LayoutSelect`}
        value={this.state.responsiveLayoutSizes[layout]}
        onChange={(value: ECRUDFormLayoutType) => {
          this.setState({ responsiveLayoutSizes: { ...this.state.responsiveLayoutSizes, [layout]: value } });
        }}
        prefix={
          <KuikaSvg
            name={
              layout === "desktop" ? "device-desktop" : layout === "tablet" ? "device-ipad-horizontal" : "device-mobile"
            }
            width={20}
            height={20}
          />
        }
        options={[
          {
            label: (
              <div
                data-testid={`${layout}SingleColumnLayout`}
                className={`${layout}SingleColumnLayout kuika-flex--gap-s`}
              >
                <img src={`/images/common/single-column.svg`} alt="Single Column" width={32} height={20} />
                <KuikaSmallText>Single Column</KuikaSmallText>
              </div>
            ),
            value: ECRUDFormLayoutType.SingleColumn
          },
          {
            label: (
              <div data-testid={`${layout}TwoColumnLayout`} className={`${layout}TwoColumnLayout kuika-flex--gap-s`}>
                <img src={`/images/common/two-columns.svg`} alt="Two Columns" width={32} height={20} />
                <KuikaSmallText>Two Columns</KuikaSmallText>
              </div>
            ),
            value: ECRUDFormLayoutType.TwoColumn
          },
          {
            label: (
              <div
                data-testid={`${layout}FourColumnsLayout`}
                className={`${layout}FourColumnsLayout kuika-flex--gap-s`}
              >
                <img src={`/images/common/four-columns.svg`} alt="Four Columns" width={32} height={20} />
                <KuikaSmallText>Four Columns</KuikaSmallText>
              </div>
            ),
            value: ECRUDFormLayoutType.FourColumn
          }
        ]}
      />
    );
  };

  render() {
    const isCrud = this.state.selectedTemplateCategory?.name === "CRUD Wizard";
    const isExcel = this.state.selectedTemplateCategory?.name === "From Excel";
    const isCrudOrExcel = isCrud || isExcel;
    const isExcelAndHasImported = isExcel && this.state.hasExcelImported;
    const isExcelAndNotHasImported = isExcel && !this.state.hasExcelImported;

    const templates = this.props.creatingScreenType === EScreenType.Master ? masterScreenTemplates : screenTemplates;
    const isMasterScreen = this.props.creatingScreenType === EScreenType.Master;
    const isOpenedFromMaster =
      this.props.sd.getScreenType() === EScreenType.Master && !this.props.isOpenedFromLeftPanel;
    return (
      <>
        <Form
          ref={this.formRef}
          name="basic"
          onFinish={this.onSubmit}
          onFinishFailed={this.onFinishFailed}
          onFieldsChange={this.handleStyleFormFieldsChange}
        >
          <KModal
            className="kuika-screens__new-screen-modal"
            header={`NEW ${isMasterScreen ? "MASTER " : ""}SCREEN`}
            hideHeader
            showModal={this.props.showModal}
            closeModal={this.closeModal}
            footer={
              <KModalFooter
                onCancel={() => this.closeModal()}
                cancelTestId="cancelButton"
                submitLabel="Create"
                submitTestId="createButton"
                onSubmit={() => {
                  this.formRef.current?.submit();
                }}
                status={this.state.saveStatus}
                statusLabels={CREATE_SCREEN_STATUS_LABELS}
              />
            }
            width={912}
          >
            <KuikaSpinnerSmall spinning={this.state.isSubmitButtonLoading} size="small">
              <div className="new-screen-modal__content" ref={this.contentRef}>
                {!isOpenedFromMaster && (
                  <div className="new-screen-modal__screen-group">
                    {templates
                      .filter((category: IScreenTemplateCategory) => category.templates?.length > 0)
                      .filter((category: IScreenTemplateCategory) => {
                        if (isOpenedFromMaster) {
                          return category.categoryName === "Blank";
                        }
                        return true;
                      })
                      .map((category: IScreenTemplateCategory) => (
                        <div key={category.categoryName}>
                          <KuikaMenuItem
                            className={`templateCategoryHeader-${category.categoryName
                              .replace(/\s/g, "")
                              .toLowerCase()}`}
                            style={{ cursor: "default" }}
                            title={
                              <KuikaTextBold>
                                {this.props.creatingScreenType === EScreenType.WebScreen &&
                                category.categoryName === "New Screen"
                                  ? "New Web Screen"
                                  : category.categoryName}
                              </KuikaTextBold>
                            }
                          />
                          {category.templates
                            .filter((template) => template.name !== "Image to Screen")
                            .map((template) => (
                              <KuikaMenuItem
                                className={`templateMenuItem-${template.name.replace(/\s/g, "").toLowerCase()}`}
                                data-testid={template.dataTestId}
                                key={template.name}
                                title={template.name}
                                selected={this.state.selectedTemplateCategory?.name === template.name}
                                onClick={() => this.setSelectedCategory(template)}
                                leftIcon={
                                  <KuikaSvg
                                    name={template.icon?.replace(".svg", "")}
                                    type="screen-templates-icons"
                                    width={16}
                                    height={16}
                                  />
                                }
                              />
                            ))}
                        </div>
                      ))}
                  </div>
                )}
                <div className="new-screen-modal__right">
                  <div className="templates__header">
                    <div className="templates__header__title">
                      <KuikaH4>{this.state.selectedTemplateCategory?.name}</KuikaH4>
                      {!isCrudOrExcel && <KuikaText>Choose a template down below to create a screen.</KuikaText>}
                      {isCrud && (
                        <KuikaText>
                          To start creating your CRUD screens, please select your database and data table first.
                        </KuikaText>
                      )}
                      {isExcel && (
                        <KuikaText>To start creating your screen, please drag or choose an excel file.</KuikaText>
                      )}
                    </div>

                    {!isCrudOrExcel && (
                      <KuikaInput
                        data-testid="searchTemplateInput"
                        placeholder="Search screen"
                        onChange={(e) => this.setState({ searchText: e.target.value })}
                        value={this.state.searchText}
                        onFocus={this.handleSearchFocus}
                        rightIcon={
                          this.state.searchText ? (
                            <KuikaSvg name="circle-x" width={12} height={12} pointer onClick={this.deleteSearchText} />
                          ) : (
                            <KuikaSvg name="search" width={12} height={12} />
                          )
                        }
                        size="md"
                      />
                    )}
                  </div>
                  {this.state.selectedTemplateCategory?.description && (
                    <div className="templates__header__description">
                      {this.state.selectedTemplateCategory?.description}
                    </div>
                  )}
                  {isCrud || isExcelAndHasImported ? (
                    <div className="crud-wizard">
                      <div className="crud-wizard__inputs">
                        <Form.Item name={["crud", "datasourceId"]} noStyle>
                          {isExcelAndHasImported ? (
                            <KuikaInput
                              className="excelImportedFileNameInput"
                              leftIcon={
                                <KuikaSvg
                                  className="excelAddedIcon"
                                  data-testid="excelAddedIcon"
                                  name="from-excel"
                                  type="screen-templates-icons"
                                  width={16}
                                  height={16}
                                />
                              }
                              rightIcon={
                                <KuikaSvg
                                  className="excelDeleteIcon"
                                  data-testid="deleteExcelIcon"
                                  name="trash"
                                  width={16}
                                  height={16}
                                  color="var(--red-5)"
                                  pointer
                                  onClick={() =>
                                    this.setState({
                                      hasExcelImported: false,
                                      importedTables: [],
                                      excelFileName: ""
                                    })
                                  }
                                />
                              }
                              defaultValue={this.getMiddleThreeDotOnOverflowTexts(this.state.excelFileName)}
                              readOnly
                            />
                          ) : (
                            <KuikaSelectbox
                              className="crudDatasourceSelect"
                              searchable
                              allowClear
                              placeholder="Choose datasource"
                              data-testid="chooseDatasourceDropdown"
                              onChange={this.handleChangeDataSource}
                              onClear={() => this.handleChangeDataSource(null)}
                              options={this.props.sd?.datasources
                                ?.filter(
                                  (ds) =>
                                    ds.datasourceType === EDatasourceType.ManagedDb ||
                                    ds.datasourceType === EDatasourceType.ManagedDbPostgre ||
                                    ds.datasourceType === EDatasourceType.ManagedDbOracle
                                )
                                ?.map((ds) => ({
                                  label: this.getDataSourceName(ds),
                                  value: ds.id
                                }))}
                            />
                          )}
                        </Form.Item>
                        <Form.Item name={["crud", "tableName"]} noStyle>
                          <KuikaSelectbox
                            className="crudTableSelect"
                            allowClear
                            searchable
                            placeholder="Choose table"
                            data-testid="chooseTableDropdown"
                            onChange={(value) => this.handleChangeTable(value as string)}
                            options={
                              isExcel
                                ? this.state.importedTables?.map((table) => ({
                                    label: table.name,
                                    value: table.name
                                  }))
                                : this.state.schema?.map((table, idx) => ({
                                    label: table.schema != null ? `${table.schema}.${table.title}` : table.title,
                                    value: table.schema != null ? `${table.schema}.${table.title}` : table.title
                                  }))
                            }
                            disabled={
                              isCrud
                                ? this.formRef.current?.getFieldsValue()?.crud?.datasourceId === undefined
                                : isExcel
                                  ? !this.state.hasExcelImported
                                  : false
                            }
                          />
                        </Form.Item>
                      </div>
                      <div className="crud-wizard__features__wrapper">
                        <KuikaSmallMediumText>List Options</KuikaSmallMediumText>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.crud?.hasListPage !== currentValues.crud?.hasListPage
                          }
                        >
                          {({ getFieldValue }) => (
                            <div
                              className={classNames(
                                "crud-wizard__field-wrapper crudListPageCheckbox",
                                getFieldValue(["crud", "hasListPage"]) &&
                                  "crud-wizard__field-wrapper--checked crudListPageCheckbox"
                              )}
                              id="crudListPageCheckbox"
                              onClick={() => {
                                const currentValue = getFieldValue(["crud", "hasListPage"]);
                                const newValue = !currentValue;
                                const formData = this.formRef.current?.getFieldsValue();

                                const updatedCrud = {
                                  ...formData.crud,
                                  hasListPage: newValue,
                                  hasDeletion: newValue,
                                  hasSearchBar: newValue,
                                  hasPagination: newValue
                                };

                                this.formRef.current?.setFieldsValue({
                                  crud: updatedCrud
                                });
                              }}
                            >
                              <Form.Item name={["crud", "hasListPage"]} valuePropName="checked" noStyle>
                                <KuikaCheckbox data-testid="listScreenCheckbox" label="List screen" />
                              </Form.Item>
                            </div>
                          )}
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.crud?.hasSearchBar !== currentValues.crud?.hasSearchBar
                          }
                        >
                          {({ getFieldValue }) => (
                            <div
                              className={classNames(
                                "crud-wizard__field-wrapper crudSearchBoxCheckbox",
                                getFieldValue(["crud", "hasSearchBar"]) &&
                                  "crud-wizard__field-wrapper--checked crudSearchBoxCheckbox"
                              )}
                              onClick={() => {
                                const currentValue = getFieldValue(["crud", "hasSearchBar"]);
                                const newValue = !currentValue;
                                const formData = this.formRef.current?.getFieldsValue();

                                const updatedCrud = {
                                  ...formData.crud,
                                  hasSearchBar: newValue,
                                  hasListPage: newValue ? true : formData.crud.hasListPage
                                };

                                this.formRef.current?.setFieldsValue({
                                  crud: updatedCrud
                                });
                              }}
                              id="crudSearchBoxCheckbox"
                            >
                              <Form.Item name={["crud", "hasSearchBar"]} valuePropName="checked" noStyle>
                                <KuikaCheckbox data-testid="searchBoxCheckbox" label="Searchbox" />
                              </Form.Item>
                            </div>
                          )}
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.crud?.hasPagination !== currentValues.crud?.hasPagination
                          }
                        >
                          {({ getFieldValue }) => (
                            <div
                              className={classNames(
                                "crud-wizard__field-wrapper crudPaginationCheckbox",
                                getFieldValue(["crud", "hasPagination"]) &&
                                  "crud-wizard__field-wrapper--checked crudPaginationCheckbox"
                              )}
                              id="crudPaginationCheckbox"
                              onClick={() => {
                                const currentValue = getFieldValue(["crud", "hasPagination"]);
                                const newValue = !currentValue;
                                const formData = this.formRef.current?.getFieldsValue();

                                const updatedCrud = {
                                  ...formData.crud,
                                  hasPagination: newValue,
                                  hasListPage: newValue ? true : formData.crud.hasListPage
                                };

                                this.formRef.current?.setFieldsValue({
                                  crud: updatedCrud
                                });
                              }}
                            >
                              <Form.Item name={["crud", "hasPagination"]} valuePropName="checked" noStyle>
                                <KuikaCheckbox data-testid="paginationCheckbox" label="Pagination" />
                              </Form.Item>
                            </div>
                          )}
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.crud?.hasDeletion !== currentValues.crud?.hasDeletion
                          }
                        >
                          {({ getFieldValue }) => (
                            <div
                              className={classNames(
                                "crud-wizard__field-wrapper crudDeleteCheckbox",
                                getFieldValue(["crud", "hasDeletion"]) &&
                                  "crud-wizard__field-wrapper--checked crudDeleteCheckbox"
                              )}
                              id="crudDeleteCheckbox"
                              onClick={() => {
                                const currentValue = getFieldValue(["crud", "hasDeletion"]);
                                const newValue = !currentValue;
                                const formData = this.formRef.current?.getFieldsValue();

                                const updatedCrud = {
                                  ...formData.crud,
                                  hasDeletion: newValue,
                                  hasListPage: newValue ? true : formData.crud.hasListPage
                                };

                                this.formRef.current?.setFieldsValue({
                                  crud: updatedCrud
                                });
                              }}
                            >
                              <Form.Item name={["crud", "hasDeletion"]} valuePropName="checked" noStyle>
                                <KuikaCheckbox data-testid="deleteCheckbox" label="Delete" />
                              </Form.Item>
                            </div>
                          )}
                        </Form.Item>
                      </div>

                      <div className="crud-wizard__section">
                        <div className="crud-wizard__features__wrapper">
                          <KuikaSmallMediumText>Options</KuikaSmallMediumText>

                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                              prevValues.crud?.hasView !== currentValues.crud?.hasView
                            }
                          >
                            {({ getFieldValue }) => (
                              <div
                                className={classNames(
                                  "crud-wizard__field-wrapper crudViewCheckbox",
                                  getFieldValue(["crud", "hasView"]) &&
                                    "crud-wizard__field-wrapper--checked crudViewCheckbox"
                                )}
                                id="crudViewCheckbox"
                                onClick={() => {
                                  const currentValue = getFieldValue(["crud", "hasView"]);
                                  this.formRef.current?.setFieldsValue({
                                    crud: { ...getFieldValue("crud"), hasView: !currentValue }
                                  });
                                }}
                              >
                                <Form.Item name={["crud", "hasView"]} valuePropName="checked" noStyle>
                                  <KuikaCheckbox data-testid="viewCheckbox" label="View" />
                                </Form.Item>
                              </div>
                            )}
                          </Form.Item>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                              prevValues.crud?.hasCreate !== currentValues.crud?.hasCreate
                            }
                          >
                            {({ getFieldValue }) => (
                              <div
                                className={classNames(
                                  "crud-wizard__field-wrapper crudCreateCheckbox",
                                  getFieldValue(["crud", "hasCreate"]) &&
                                    "crud-wizard__field-wrapper--checked crudCreateCheckbox"
                                )}
                                id="crudCreateCheckbox"
                                onClick={() => {
                                  const currentValue = getFieldValue(["crud", "hasCreate"]);
                                  this.formRef.current?.setFieldsValue({
                                    crud: { ...getFieldValue("crud"), hasCreate: !currentValue }
                                  });
                                }}
                              >
                                <Form.Item name={["crud", "hasCreate"]} valuePropName="checked" noStyle>
                                  <KuikaCheckbox data-testid="createCheckbox" label="Create" />
                                </Form.Item>
                              </div>
                            )}
                          </Form.Item>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                              prevValues.crud?.hasUpdate !== currentValues.crud?.hasUpdate
                            }
                          >
                            {({ getFieldValue }) => (
                              <div
                                className={classNames(
                                  "crud-wizard__field-wrapper crudUpdateCheckbox",
                                  getFieldValue(["crud", "hasUpdate"]) &&
                                    "crud-wizard__field-wrapper--checked crudUpdateCheckbox"
                                )}
                                id="crudUpdateCheckbox"
                                onClick={() => {
                                  const currentValue = getFieldValue(["crud", "hasUpdate"]);
                                  this.formRef.current?.setFieldsValue({
                                    crud: { ...getFieldValue("crud"), hasUpdate: !currentValue }
                                  });
                                }}
                              >
                                <Form.Item name={["crud", "hasUpdate"]} valuePropName="checked" noStyle>
                                  <KuikaCheckbox data-testid="updateCheckbox" label="Update" />
                                </Form.Item>
                              </div>
                            )}
                          </Form.Item>
                        </div>
                      </div>
                      <div className="crud-wizard__navigation-type">
                        <KuikaSmallMediumText>Navigation</KuikaSmallMediumText>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.crud?.navigationType !== currentValues.crud?.navigationType
                          }
                        >
                          {({ getFieldValue, setFieldsValue }) => (
                            <KuikaTabs
                              className="crudNavTabs"
                              options={[
                                {
                                  label: (
                                    <span data-testid="drawerTab" className="crudNavDrawerTab">
                                      Drawer
                                    </span>
                                  ),
                                  value: ECRUDFormNavigationType.Drawer.toString()
                                },
                                {
                                  label: (
                                    <span data-testid="modalPopupTab" className="crudNavModalTab">
                                      Modal / Pop-up
                                    </span>
                                  ),
                                  value: ECRUDFormNavigationType.Modal.toString()
                                },
                                {
                                  label: (
                                    <span data-testid="currentScreenTab" className="crudNavCurrentScreenTab">
                                      Current screen
                                    </span>
                                  ),
                                  value: ECRUDFormNavigationType.CurrentPage.toString()
                                }
                              ]}
                              value={
                                getFieldValue(["crud", "navigationType"])?.toString() ||
                                ECRUDFormNavigationType.Drawer.toString()
                              }
                              onChange={(value) => {
                                setFieldsValue({
                                  crud: {
                                    ...getFieldValue("crud"),
                                    navigationType: parseInt(value)
                                  }
                                });
                              }}
                            />
                          )}
                        </Form.Item>
                      </div>
                      <div className="crud-wizard__features__wrapper">
                        <KuikaSmallMediumText>Layout</KuikaSmallMediumText>
                        <div className="crud-wizard__layout-options">
                          {this.renderLayoutOption("desktop")}
                          {this.renderLayoutOption("tablet")}
                          {this.renderLayoutOption("mobile")}
                        </div>
                      </div>
                    </div>
                  ) : isExcelAndNotHasImported ? (
                    <div className="crud-wizard__excel-import-wrapper">
                      <KuikaSpinnerSmall spinning={this.state.isFileImporting} size="small">
                        <KuikaFileInput
                          variant="dropzone"
                          inputId="excelImportDragger"
                          onFiles={this.handleExcelFiles}
                          accept={".xlsx, .xls"}
                          className="crud-wizard__excel-import"
                        >
                          <KuikaText className="excelImportDragText crud-wizard__excel-import__drag-and-drop">
                            Drag and drop here or{" "}
                          </KuikaText>
                          <KuikaText
                            id="excelImportChooseFromComputerText"
                            className="crud-wizard__excel-import__choose-from-computer"
                          >
                            choose from computer
                          </KuikaText>
                        </KuikaFileInput>
                      </KuikaSpinnerSmall>
                    </div>
                  ) : (
                    <div className="new-screen-modal__templates">
                      {this.state.selectedTemplateCategory?.items
                        ?.filter((template) => {
                          if (this.props.sd.project?.projectType === EProjectType.Desktop && template.isMobile) {
                            return false;
                          }
                          if (
                            this.props.sd.project?.projectType === EProjectType.Mobile &&
                            template.isDesktop &&
                            this.props.creatingScreenType !== EScreenType.WebScreen
                          ) {
                            return false;
                          }
                          return true;
                        })
                        ?.filter((template) => {
                          if (this.state.searchText) {
                            return template.name.toLowerCase().includes(this.state.searchText.toLowerCase());
                          }
                          return true;
                        })
                        ?.filter((template) => {
                          if (this.state.selectedDeviceType === EAppSupportType.Web) {
                            return template.isDesktop;
                          }
                          if (this.state.selectedDeviceType === EAppSupportType.Mobile) {
                            return template.isMobile;
                          }
                          return true;
                        })
                        ?.filter((template) => {
                          if (isOpenedFromMaster) {
                            return template.name === "Blank";
                          }
                          return true;
                        })
                        ?.map((template) => {
                          return (
                            <div
                              data-testid={`${template.name.toLowerCase().replace(/\s/g, "")}TemplateCard`}
                              className={classNames(
                                `templates__item templateCard-${template.name.toLowerCase().replace(/\s/g, "")}`,
                                this.state.selectedTemplate?.name === template.name &&
                                  `templates__item-selected templateCard-${template.name
                                    .toLowerCase()
                                    .replace(/\s/g, "")}`
                              )}
                              onClick={() => this.selectScreenDesignTemplate(template)}
                              key={template.name}
                              id={`templateCard-${template.name.toLowerCase().replace(/\s/g, "")}`}
                            >
                              <div className="templates__item__image-wrapper">
                                <img src={`/images/screen-templates/${template.iconPath}`} alt={template.name} />
                                {template.isDesktop ? (
                                  <KuikaBadge
                                    color="blue"
                                    content={<KuikaSvg name="device-imac" width={16} height={16} />}
                                    type="icon"
                                  />
                                ) : template.isMobile ? (
                                  <KuikaBadge
                                    color="blue"
                                    content={<KuikaSvg name="device-mobile" width={16} height={16} />}
                                    type="icon"
                                  />
                                ) : (
                                  <KuikaBadge
                                    color="blue"
                                    content={<KuikaSvg name="devices" width={16} height={16} />}
                                    type="icon"
                                  />
                                )}
                              </div>
                              <div className="templates__item__title-wrapper">
                                <KuikaSmallMediumText>
                                  {this.state.selectedTemplateCategory?.name} template
                                </KuikaSmallMediumText>
                                <KuikaTextMedium>{template.name}</KuikaTextMedium>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                  <div className="new-screen-modal__properties">
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: "Please enter a name" },
                        {
                          pattern: new RegExp("^[A-Za-z][A-Za-z0-9 ]+$"),
                          message: "Field accepts only english letters!"
                        }
                      ]}
                    >
                      <KuikaInput
                        ref={this.nameInputRef}
                        className="screenNameInput"
                        data-testid="screenNameInput"
                        placeholder="Screen name"
                        autoFocus
                        onPressEnter={() => {
                          this.formRef.current?.submit();
                        }}
                        size="lg"
                      />
                    </Form.Item>
                    {this.props.sd.getScreenType() !== EScreenType.Report &&
                      this.props.context.isMasterScreenActive &&
                      !isMasterScreen &&
                      (this.state.selectedTemplate?.name === "Blank" ||
                        this.state.selectedTemplate?.name === "Blank With Header") &&
                      this.props.sd.screenList?.filter((x) => x.publicMetaData?.screenType === EScreenType.Master)
                        ?.length > 0 && (
                        <Form.Item name="masterId">
                          <KuikaSelectbox
                            className="masterScreenSelect"
                            placeholder="Choose a master screen"
                            size="lg"
                            allowClear={this.props.isOpenedFromLeftPanel}
                            defaultValue={
                              this.props.sd.getScreenType() === EScreenType.Master && !this.props.isOpenedFromLeftPanel
                                ? this.props.sd.sdId
                                : undefined
                            }
                            disabled={
                              this.props.sd.getScreenType() === EScreenType.Master && !this.props.isOpenedFromLeftPanel
                            }
                            options={this.props.sd.screenList
                              ?.filter((x) => x.publicMetaData?.screenType === EScreenType.Master)
                              ?.sort((a, b) => a.name.localeCompare(b.name))
                              ?.map((screen, index) => ({
                                value: screen.id,
                                label: screen.name
                              }))}
                          />
                        </Form.Item>
                      )}
                    {this.state.selectedTemplate?.name === "Blank" && !isMasterScreen && (
                      <KuikaSelectbox
                        className="roleAuthorizationSelect"
                        size="lg"
                        onChange={this.onAuthChange}
                        value={this.state.selectedAuth}
                        placeholder="Choose an authorization"
                        options={[
                          { value: EScreenAuthorizationType.AllRoles, label: "All Roles Access" },
                          { value: EScreenAuthorizationType.Anonymous, label: "Anonymous Access" }
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </KuikaSpinnerSmall>
          </KModal>
        </Form>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const screenAddComponent = connect(
  null,
  mapDispatchToProps
)(withContext<IScreenAddProps>(withHistory<IScreenAddProps>(withSD<IScreenAddProps>(ScreenAddComponent))));
export { screenAddComponent as ScreenAddComponent };
